import { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import ActionButton from "Components/atoms/buttons/action-button"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import SearchFormContainer from "Components/atoms/containers/search-form-container"
import FiscalCalendar from "utils/fiscal-calendar"
import SearchSelect from "Components/molecule/inputs/search-select"
import { ExportIcon, HelpIcon } from "Components/atoms/icons"
import FinancialKpiTemplate from "Components/templates/financial-kpi"
import useManual from "hooks/useManual"
import useFiscalYearOptions from "hooks/useFiscalYearOptions"
import { FINANCIAL_KPI_PATHS, FinancialKpiSection } from "models/financial-kpi"
import Screen, { SCREENS } from "models/screen"
import { PermissionsSliceState } from "slices/auth/permissions/reducer"
import { FinancialKpiSliceState, setFinancialKpiFiscalYear, setFinancialKpiSection } from "slices/finance/financial-kpi/reducer"
import { downloadFinancialKpiExport, getFinancialKpiSection, saveFinancialKpiSnapshot } from "slices/finance/financial-kpi/thunk"

const selectFinancialKpi = createSelector(
    (state: any) => state.FinancialKpi,
    ({ fiscalYear, data }: FinancialKpiSliceState) => ({ fiscalYear, data })
)

const selectPermissions = createSelector(
    (state: any) => state.Permissions,
    ({ screens }: PermissionsSliceState) => ({ screens })
)

const SECTIONS = Object.keys(FINANCIAL_KPI_PATHS) as FinancialKpiSection[]

const FinancialKPIPage = () => {
    const dispatch: any = useDispatch()
    const { fiscalYear, data } = useSelector(selectFinancialKpi)
    const { screens } = useSelector(selectPermissions)
    const { options: fiscalYearOptions } = useFiscalYearOptions(fiscalYear)
    const { handleOpenManual } = useManual("finance/financial-kpi")

    const [isSavingSnapshot, setIsSavingSnapshot] = useState(false)
    const [isExporting, setIsExporting] = useState(false)

    const canSaveSnapshot = useMemo(() => screens.some((s: Screen) =>
        s.code === SCREENS.FINANCE_FINANCIAL_KPI_SNAPSHOT || s.code === SCREENS.ADMIN), [screens])

    // Year the screen is showing now; a response for a year the user already left is dropped.
    const shownYear = useRef(fiscalYear)

    // Every container loads on its own, so a slow one never blocks the others.
    const loadSection = async (section: FinancialKpiSection, year: number) => {
        const res = await getFinancialKpiSection(section, year)
        if (res && shownYear.current === year) dispatch(setFinancialKpiSection({ section, data: res }))
    }

    useEffect(() => {
        shownYear.current = fiscalYear
        SECTIONS.forEach(section => loadSection(section, fiscalYear))
    }, [fiscalYear])

    const onSaveSnapshot = async () => {
        setIsSavingSnapshot(true)
        if (await saveFinancialKpiSnapshot()) {
            await Promise.all((["funnel", "wih", "snapshots"] as FinancialKpiSection[]).map(s => loadSection(s, fiscalYear)))
        }
        setIsSavingSnapshot(false)
    }

    const onExport = async () => {
        setIsExporting(true)
        await downloadFinancialKpiExport(fiscalYear)
        setIsExporting(false)
    }

    return <FinancialKpiTemplate
        fiscalYear={fiscalYear}
        data={data}
        canSaveSnapshot={canSaveSnapshot}
        isSavingSnapshot={isSavingSnapshot}
        onSaveSnapshot={onSaveSnapshot}
        actions={<>
            <ActionButton onClick={onExport} disabled={isExporting}>
                {isExporting ? "Exporting..." : "Export"} <ExportIcon />
            </ActionButton>
            <ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>
        </>}
        filters={<SearchFormContainer
            compact
            inputs={<SearchSelect
                containerStyle={{ flex: "0 1 220px", minWidth: "160px" }}
                label="Fiscal Year"
                options={fiscalYearOptions}
                value={fiscalYearOptions.find(o => o.value === String(fiscalYear)) ?? null}
                onChange={v => v && dispatch(setFinancialKpiFiscalYear(Number(v.value)))}
                placeholder="Select year"
            />}
            clearSearchButton={<SecondaryButton onClick={() => dispatch(setFinancialKpiFiscalYear(FiscalCalendar.currentFiscalYear()))}>
                Current Year
            </SecondaryButton>}
        />}
    />
}

export default FinancialKPIPage
